import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  type MutableRefObject,
} from "react";
import { createPortal } from "react-dom";
import RedoIcon from "../../../assets/svg/v1/redo-icon";
import UndoIcon from "../../../assets/svg/v1/undo-icon";
import CheckIcon from "../../../assets/svg/v1/check-icon";
import ChevronDownSolidIcon from "../../../assets/svg/v1/chevron-down-solid-icon";
import FontSizeIcon from "../../../assets/svg/v1/font-size-icon";
import TextColorPickerIcon from "../../../assets/svg/v1/text-color-picker-icon";
import AlignLeftIcon from "../../../assets/svg/v1/align-left-icon";
import AlignCenterIcon from "../../../assets/svg/v1/align-center-icon";
import AlignRightIcon from "../../../assets/svg/v1/align-right-icon";
import IndentLeftIcon from "../../../assets/svg/v1/indent-left-icon";
import IndentRightIcon from "../../../assets/svg/v1/indent-right-icon";
import ImageIcon from "../../../assets/svg/v1/image-icon";
import { TextSelection } from "prosemirror-state";
import { v4 as uuidv4 } from "uuid";
import { customToast } from "../../../utils/custom-toast/custom-toast";
import MentionsIcon from "../../../assets/svg/mentions-icon";
import BoldIcon from "../../../assets/svg/bold-icon";
import ItalicsIcon from "../../../assets/svg/italics-icon";
import UnderlineIcon from "../../../assets/svg/underline-icon";
import StrikeThroughIcon from "../../../assets/svg/strikethrough-icon";
import UnorderedListIcon from "../../../assets/svg/unordered-list-icon";
import NumberedListIcon from "../../../assets/svg/numbered-list-icon";
import LinkIcon from "../../../assets/svg/link-icon";
import AttachmentIcon from "../../../assets/svg/v1/attachment-icon";
import EmojiIcon from "../../../assets/svg/emoji-icon";
import { visibleScrollbar } from "../../../utils/functions/get-visible-scrollbar";
import { Avatar } from "../../Avatar/Avatar";
import { Attachment, RichTextEditorToolbarProps } from "../utils/types/types";
import ColorPicker from "./ColorPicker";
import EmojiPicker from "./EmojiPicker";
import {
  alignOptions,
  defaultToolbarSchema,
  fontOptions,
  formattingOptions,
  listOptions,
  MAX_IMAGE_SIZE_BYTES,
  MAX_IMAGE_SIZE_MB,
  sizeOptions,
} from "../utils/constants/constants";
import { formatBytes } from "../utils/functions/get-formatted-bytes";
import { Editor } from "@tiptap/core";
import { Tooltip } from "../../Tooltip/Tooltip";

const useToolbarPortal = (
  isOpen: boolean,
  triggerRef: React.RefObject<HTMLElement>,
  onClose: () => void,
  dropdownWidth: number = 200,
  dropdownHeightHint: number = 300
) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [placement, setPlacement] = useState<"top" | "bottom">("bottom");
  const [positioned, setPositioned] = useState(false);
  const portalRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const buffer = 8;

    const actualH = portalRef.current?.offsetHeight || dropdownHeightHint;

    const spaceBelow = viewportHeight - rect.bottom - buffer;
    const spaceAbove = rect.top - buffer;
    const spaceRight = viewportWidth - rect.left - buffer;
    const spaceLeft = rect.left - buffer;

    const shouldPlaceAbove = spaceBelow < actualH && spaceAbove >= actualH;
    const shouldPlaceLeft =
      spaceRight < dropdownWidth && spaceLeft > dropdownWidth;

    setPlacement(shouldPlaceAbove ? "top" : "bottom");

    let top: number;
    let left: number;

    if (shouldPlaceAbove) {
      top = rect.top - actualH - buffer;
    } else {
      top = rect.bottom + buffer;
    }

    if (shouldPlaceLeft) {
      left = rect.right - dropdownWidth;
    } else {
      left = rect.left;
    }

    left = Math.max(
      buffer,
      Math.min(left, viewportWidth - dropdownWidth - buffer)
    );
    top = Math.max(buffer, Math.min(top, viewportHeight - actualH - buffer));

    setPosition({ top, left });
    setPositioned(true);
  }, [triggerRef, dropdownWidth, dropdownHeightHint]);

  useEffect(() => {
    if (!isOpen) {
      setPositioned(false);
      return;
    }
    if (!triggerRef.current) return;

    updatePosition();

    const handleUpdate = () => updatePosition();
    window.addEventListener("resize", handleUpdate);

    const blockScroll = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener("scroll", blockScroll, {
      passive: false,
      capture: true,
    });
    window.addEventListener("wheel", blockScroll, {
      passive: false,
      capture: true,
    });
    window.addEventListener("touchmove", blockScroll, {
      passive: false,
      capture: true,
    });

    return () => {
      window.removeEventListener("resize", handleUpdate);
      window.removeEventListener("scroll", blockScroll, true);
      window.removeEventListener("wheel", blockScroll, true);
      window.removeEventListener("touchmove", blockScroll, true);
    };
  }, [isOpen, triggerRef, updatePosition]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (triggerRef.current && !triggerRef.current.contains(target)) {
        const portalElements = document.querySelectorAll(
          "[data-toolbar-portal]"
        );
        let isInsidePortal = false;

        for (const element of portalElements) {
          if (element.contains(target)) {
            isInsidePortal = true;
            break;
          }
        }

        if (!isInsidePortal) {
          onClose();
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose, triggerRef]);

  const portalRefCallback = useCallback(
    (node: HTMLDivElement | null) => {
      (portalRef as MutableRefObject<HTMLDivElement | null>).current = node;
      if (node) requestAnimationFrame(updatePosition);
    },
    [updatePosition]
  );

  const renderPortal = (children: React.ReactNode) => {
    if (!isOpen) return null;

    return createPortal(
      <div
        ref={portalRefCallback}
        data-toolbar-portal
        style={{
          position: "fixed",
          top: position.top,
          left: position.left,
          zIndex: 9999,
          visibility: positioned ? "visible" : "hidden",
        }}
      >
        {children}
      </div>,
      document.body
    );
  };

  return { renderPortal, placement };
};

const Toolbar = ({
  editor,
  onAttachmentsChange,
  onImageInsert,
  schema = defaultToolbarSchema,
  toolbarExtraClass,
  mentionActiveUsersList,
  setTaggedUsers,
  groupedToolbarOrder = false,
}: RichTextEditorToolbarProps) => {
  const [isFontOpen, setIsFontOpen] = useState(false);
  const [isSizeOpen, setIsSizeOpen] = useState(false);
  const [isColorOpen, setIsColorOpen] = useState(false);
  const [isListOpen, setIsListOpen] = useState(false);
  const [isAlignOpen, setIsAlignOpen] = useState(false);
  const [isLinkPopoverOpen, setIsLinkPopoverOpen] = useState(false);
  const [isEmojiOpen, setIsEmojiOpen] = useState(false);
  const [isProcessingAttachments, setIsProcessingAttachments] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");
  const [selectedFont, setSelectedFont] = useState("sans-serif");
  const [selectedSize, setSelectedSize] = useState("normal");
  const [selectedList, setSelectedList] = useState("bullet");
  const [selectedAlign, setSelectedAlign] = useState("left");
  const fontDropdownRef = useRef<HTMLDivElement>(null);
  const sizeDropdownRef = useRef<HTMLDivElement>(null);
  const colorDropdownRef = useRef<HTMLDivElement>(null);
  const listDropdownRef = useRef<HTMLDivElement>(null);
  const alignDropdownRef = useRef<HTMLDivElement>(null);
  const linkPopoverRef = useRef<HTMLDivElement>(null);
  const emojiDropdownRef = useRef<HTMLDivElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const [linkUrlError, setLinkUrlError] = useState<string>("");
  const [isMentionsOpen, setIsMentionsOpen] = useState(false);
  const mentionsRef = useRef<HTMLDivElement>(null);

  const [activeFormatting, setActiveFormatting] = useState<{
    bold: boolean;
    italic: boolean;
    underline: boolean;
    strike: boolean;
    color: string | null;
    fontFamily: string | null;
    fontSize: string | null;
  }>({
    bold: false,
    italic: false,
    underline: false,
    strike: false,
    color: null,
    fontFamily: null,
    fontSize: null,
  });

  const calculateTotalImageSize = useCallback(() => {
    if (!editor) return 0;

    let totalSize = 0;
    const { doc } = editor.state;

    doc.descendants((node) => {
      if (node.type.name === "image" && node.attrs.src) {
        if (node.attrs.src.startsWith("data:")) {
          const base64Data = node.attrs.src.split(",")[1];
          if (base64Data) {
            totalSize += Math.ceil((base64Data.length * 3) / 4);
          }
        }
      }
      return true;
    });

    return totalSize;
  }, [editor]);

  const mergedSchema = { ...defaultToolbarSchema, ...schema };
  const typographySchema = {
    ...defaultToolbarSchema.typography,
    ...schema.typography,
  };
  const textFormattingSchema = {
    ...defaultToolbarSchema.textFormatting,
    ...schema.textFormatting,
  };
  const textStylingSchema = {
    ...defaultToolbarSchema.textStyling,
    ...schema.textStyling,
  };
  const contentStructureSchema = {
    ...defaultToolbarSchema.contentStructure,
    ...schema.contentStructure,
  };
  const mediaSchema = { ...defaultToolbarSchema.media, ...schema.media };
  const interactiveSchema = {
    ...defaultToolbarSchema.interactive,
    ...schema.interactive,
  };
  const utilitiesSchema = {
    ...defaultToolbarSchema.utilities,
    ...schema.utilities,
  };
  const mentionsSchema = {
    ...defaultToolbarSchema.mentions,
    ...schema.mentions,
  };

  const fontPortal = useToolbarPortal(isFontOpen, fontDropdownRef, () =>
    setIsFontOpen(false)
  );
  const sizePortal = useToolbarPortal(isSizeOpen, sizeDropdownRef, () =>
    setIsSizeOpen(false)
  );
  const colorPortal = useToolbarPortal(isColorOpen, colorDropdownRef, () =>
    setIsColorOpen(false)
  );
  const listPortal = useToolbarPortal(
    isListOpen,
    listDropdownRef,
    () => setIsListOpen(false),
    60,
    60
  );
  const alignPortal = useToolbarPortal(
    isAlignOpen,
    alignDropdownRef,
    () => setIsAlignOpen(false),
    60,
    90
  );
  const linkPortal = useToolbarPortal(
    isLinkPopoverOpen,
    linkPopoverRef,
    () => setIsLinkPopoverOpen(false),
    320,
    130
  );
  const emojiPortal = useToolbarPortal(
    isEmojiOpen,
    emojiDropdownRef,
    () => setIsEmojiOpen(false),
    300,
    400
  );
  const mentionsPortal = useToolbarPortal(isMentionsOpen, mentionsRef, () =>
    setIsMentionsOpen(false)
  );

  useEffect(() => {
    if (editor) {
      if (editor.isActive("bulletList")) {
        setSelectedList("bullet");
      } else if (editor.isActive("orderedList")) {
        setSelectedList("number");
      }
    }
  }, [editor, editor?.state.selection]);

  useEffect(() => {
    if (editor) {
      const currentFontSize =
        editor.getAttributes("textStyle")?.fontSize || null;

      if (currentFontSize) {
        const matchingSizeOption = sizeOptions.find(
          (option) => option.fontSize === currentFontSize
        );
        if (matchingSizeOption) {
          setSelectedSize(matchingSizeOption.value);
        }
      } else {
        setSelectedSize("normal");
      }

      setActiveFormatting({
        bold: editor.isActive("bold"),
        italic: editor.isActive("italic"),
        underline: editor.isActive("underline"),
        strike: editor.isActive("strike"),
        color: editor.getAttributes("color")?.color || null,
        fontFamily: editor.getAttributes("textStyle")?.fontFamily || null,
        fontSize: currentFontSize,
      });
    }
  }, [editor, editor?.state.selection]);

  const handleFontChange = (fontValue: string) => {
    setSelectedFont(fontValue);
    setActiveFormatting((prev) => ({ ...prev, fontFamily: fontValue }));
    editor.chain().focus().setFontFamily(fontValue).run();
    setIsFontOpen(false);
  };

  const handleSizeChange = (sizeValue: string) => {
    setSelectedSize(sizeValue);
    const sizeOption = sizeOptions.find((option) => option.value === sizeValue);
    if (sizeOption) {
      setActiveFormatting((prev) => ({
        ...prev,
        fontSize: sizeOption.fontSize,
      }));
      editor.chain().focus().setFontSize(sizeOption.fontSize).run();
    }
    setIsSizeOpen(false);
  };

  const handleListChange = (listValue: string) => {
    setSelectedList(listValue);

    if (listValue === "bullet") {
      if (editor.isActive("bulletList")) {
        setIsListOpen(false);
        return;
      }

      if (editor.isActive("orderedList")) {
        editor.chain().focus().toggleOrderedList().toggleBulletList().run();
      } else {
        editor.chain().focus().toggleBulletList().run();
      }
    } else if (listValue === "number") {
      if (editor.isActive("orderedList")) {
        setIsListOpen(false);
        return;
      }

      if (editor.isActive("bulletList")) {
        editor.chain().focus().toggleBulletList().toggleOrderedList().run();
      } else {
        editor.chain().focus().toggleOrderedList().run();
      }
    }
    setIsListOpen(false);
  };

  const handleAlignChange = (alignValue: string) => {
    setSelectedAlign(alignValue);

    const { from, to } = editor.state.selection;
    const hasSelection = from !== to;

    if (hasSelection) {
      editor
        .chain()
        .focus()
        .setTextAlign(alignValue as "left" | "center" | "right")
        .run();
    } else {
      const { doc } = editor.state;

      const fullRange = { from: 0, to: doc.content.size };

      editor
        .chain()
        .focus()
        .setTextSelection(fullRange)
        .setTextAlign(alignValue as "left" | "center" | "right")
        .run();

      const tr = editor.state.tr;
      let modified = false;

      doc.descendants((node, pos) => {
        if (node.type.name === "image") {
          tr.setNodeMarkup(
            pos,
            undefined,
            {
              ...node.attrs,
              textAlign: alignValue,
            },
            undefined
          );
          modified = true;
        }
        return true;
      });

      if (modified) {
        editor.view.dispatch(tr);
      }

      editor.chain().focus().setTextSelection(0).run();
    }

    setIsAlignOpen(false);
  };

  const handleSetColor = (color: string) => {
    const { from, to } = editor.state.selection;
    setActiveFormatting((prev) => ({ ...prev, color }));
    editor
      .chain()
      .focus()
      .setTextSelection({ from, to })
      .setColor(color)
      .run();
    setIsColorOpen(false);
  };

  const handleUnsetColor = () => {
    const { from, to } = editor.state.selection;
    setActiveFormatting((prev) => ({ ...prev, color: null }));
    editor
      .chain()
      .focus()
      .setTextSelection({ from, to })
      .unsetColor()
      .run();
    setIsColorOpen(false);
  };

  const handleSetHighlight = (color: string) => {
    editor.chain().toggleHighlight({ color }).run();
    setIsColorOpen(false);
  };

  const handleUnsetHighlight = () => {
    editor.chain().unsetHighlight().run();
    setIsColorOpen(false);
  };

  const validateUrl = useCallback(
    (url: string): { isValid: boolean; error?: string } => {
      const trimmedUrl = url.trim();

      if (!trimmedUrl) {
        return { isValid: false, error: "URL is required" };
      }

      try {
        const urlObj = new URL(trimmedUrl);
        const validProtocols = ["http:", "https:", "mailto:", "tel:", "ftp:"];
        if (!validProtocols.includes(urlObj.protocol)) {
          return {
            isValid: false,
            error:
              "Invalid protocol. Use http://, https://, mailto:, tel:, or ftp://",
          };
        }

        if (
          (urlObj.protocol === "http:" || urlObj.protocol === "https:") &&
          !urlObj.hostname
        ) {
          return { isValid: false, error: "Invalid URL format" };
        }

        return { isValid: true };
      } catch {
        let fixedUrl = trimmedUrl;

        if (!/^[a-zA-Z]+:\/\//.test(fixedUrl)) {
          fixedUrl = "https://" + fixedUrl;
        }

        try {
          new URL(fixedUrl);
          return { isValid: true };
        } catch {
          return { isValid: false, error: "Please enter a valid URL" };
        }
      }
    },
    []
  );

  const handleLinkButtonClick = useCallback(() => {
    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to, " ");
    setLinkText(selectedText);
    setLinkUrl("");
    setLinkUrlError("");
    setIsLinkPopoverOpen((prev) => !prev);
  }, [editor]);

  const handleApplyLink = useCallback(() => {
    const url = linkUrl.trim();
    const text = linkText.trim();
    const { from, to } = editor.state.selection;
    const range = { from, to };

    if (!url || !text) {
      if (!url) {
        setLinkUrlError("URL is required");
      }
      if (!text) {
        return;
      }
      return;
    }

    const validation = validateUrl(url);
    if (!validation.isValid) {
      setLinkUrlError(validation.error || "Invalid URL");
      return;
    }

    setLinkUrlError("");

    let normalizedUrl = url;
    if (!/^[a-zA-Z]+:\/\//.test(normalizedUrl)) {
      normalizedUrl = "https://" + normalizedUrl;
    }

    editor
      .chain()
      .focus()
      .insertContentAt(range, {
        type: "text",
        text: text,
        marks: [
          {
            type: "link",
            attrs: { href: normalizedUrl },
          },
        ],
      })
      .setTextSelection(range.from + text.length)
      .unsetMark("link")
      .run();

    setIsLinkPopoverOpen(false);
  }, [editor, linkUrl, linkText, validateUrl]);

  const handleLinkUrlChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newUrl = e.target.value;
      setLinkUrl(newUrl);

      if (linkUrlError) {
        setLinkUrlError("");
      }
    },
    [linkUrlError]
  );

  const handleImageUpload = useCallback(() => {
    if (imageInputRef.current) {
      imageInputRef.current.click();
    }
  }, []);

  const handleImageFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file || !editor) {
        if (imageInputRef.current) {
          imageInputRef.current.value = "";
        }
        return;
      }

      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        customToast(
          `Image size (${formatBytes(
            file.size
          )}) exceeds the maximum allowed size of ${MAX_IMAGE_SIZE_MB}MB`,
          "error"
        );
        if (imageInputRef.current) {
          imageInputRef.current.value = "";
        }
        return;
      }

      const currentTotalSize = calculateTotalImageSize();
      const newTotalSize = currentTotalSize + file.size;

      if (newTotalSize > MAX_IMAGE_SIZE_BYTES) {
        const remainingSize = MAX_IMAGE_SIZE_BYTES - currentTotalSize;
        customToast(
          `Adding this image would exceed the ${MAX_IMAGE_SIZE_MB}MB limit. ` +
            `Current usage: ${formatBytes(currentTotalSize)}. ` +
            `Remaining: ${formatBytes(remainingSize)}`,
          "error"
        );
        if (imageInputRef.current) {
          imageInputRef.current.value = "";
        }
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        if (!e.target?.result) {
          return;
        }
        const dataUrl = e.target.result as string;
        const img = new window.Image();
        img.src = dataUrl;
        img.onload = () => {
          const editorWidth = editor.view.dom.clientWidth;
          const { naturalWidth } = img;

          const width =
            naturalWidth > editorWidth ? "100%" : `${naturalWidth}px`;

          editor
            .chain()
            .focus()
            .insertContent({
              type: "image",
              attrs: {
                src: dataUrl,
                width,
                originalSize: file.size,
              },
            })
            .command(({ tr }) => {
              const pos = tr.selection.to;
              tr.setSelection(TextSelection.create(tr.doc, pos));
              return true;
            })
            .focus()
            .run();

          // Kick off S3 upload AFTER the image is in the editor so we have
          // the dataUrl to swap when the S3 URL comes back.
          onImageInsert?.(file, (s3Url: string) => {
            if (editor.isDestroyed) return;
            const { state } = editor;
            const { tr } = state;
            let modified = false;
            state.doc.descendants((node, pos) => {
              if (node.type.name === "image" && node.attrs.src === dataUrl) {
                tr.setNodeMarkup(pos, undefined, { ...node.attrs, src: s3Url });
                modified = true;
              }
            });
            if (modified) editor.view.dispatch(tr);
          });
        };
      };
      reader.readAsDataURL(file);

      if (imageInputRef.current) {
        imageInputRef.current.value = "";
      }
    },
    [editor, calculateTotalImageSize, onImageInsert]
  );

  const handleAttachmentFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files || []);

      if (files.length === 0) return;

      setIsProcessingAttachments(true);
      const processFiles = async () => {
        try {
          const uploaded: Attachment[] = [];

          for (const file of files) {
            uploaded.push({
              id: uuidv4(),
              name: file.name,
              size: file.size,
              type: file.type,
              data: file,
            });
          }
          onAttachmentsChange?.(uploaded);
        } catch (error) {
          console.error("Error processing attachments:", error);
        } finally {
          setIsProcessingAttachments(false);
        }
      };

      processFiles();

      if (attachmentInputRef.current) {
        attachmentInputRef.current.value = "";
      }
    },
    [onAttachmentsChange]
  );

  const handleAttachmentClick = useCallback(() => {
    if (attachmentInputRef.current) {
      attachmentInputRef.current.click();
    }
  }, []);

  const handleIndent = () => {
    const { from, to } = editor.state.selection;
    const hasSelection = from !== to;

    if (hasSelection) {
      editor.chain().focus().indent().run();
    } else {
      const { doc } = editor.state;

      const tr = editor.state.tr;
      let modified = false;

      doc.descendants((node, pos) => {
        if (node.isBlock || node.type.name === "image") {
          const currentLevel = node.attrs.indentLevel || 0;
          if (currentLevel < 100) {
            tr.setNodeMarkup(
              pos,
              undefined,
              {
                ...node.attrs,
                indentLevel: currentLevel + 1,
              },
              undefined
            );
            modified = true;
          }
        }
        return true;
      });

      if (modified) {
        const currentPos = editor.state.selection.to;

        tr.setSelection(TextSelection.create(tr.doc, currentPos));

        editor.view.dispatch(tr);
      } else {
        return;
      }
    }
  };

  const handleOutdent = () => {
    const { from, to } = editor.state.selection;
    const hasSelection = from !== to;

    if (hasSelection) {
      editor.chain().focus().outdent().run();
    } else {
      const { doc } = editor.state;

      const tr = editor.state.tr;
      let modified = false;

      doc.descendants((node, pos) => {
        if (node.isBlock || node.type.name === "image") {
          const currentLevel = node.attrs.indentLevel || 0;
          if (currentLevel > 0) {
            tr.setNodeMarkup(
              pos,
              undefined,
              {
                ...node.attrs,
                indentLevel: currentLevel - 1,
              },
              undefined
            );
            modified = true;
          }
        }
        return true;
      });

      if (modified) {
        const currentPos = editor.state.selection.to;

        tr.setSelection(TextSelection.create(tr.doc, currentPos));

        editor.view.dispatch(tr);
      } else {
        return;
      }
    }
  };

  const handleEmojiToggle = () => {
    setIsEmojiOpen(!isEmojiOpen);
  };

  const handleBoldToggle = () => {
    const newBoldState = !activeFormatting.bold;
    setActiveFormatting((prev) => ({ ...prev, bold: newBoldState }));

    if (newBoldState) {
      editor.chain().focus().toggleBold().run();
    } else {
      editor.chain().focus().unsetBold().run();
    }
  };

  const handleItalicToggle = () => {
    const newItalicState = !activeFormatting.italic;
    setActiveFormatting((prev) => ({ ...prev, italic: newItalicState }));

    if (newItalicState) {
      editor.chain().focus().toggleItalic().run();
    } else {
      editor.chain().focus().unsetItalic().run();
    }
  };

  const handleUnderlineToggle = () => {
    const newUnderlineState = !activeFormatting.underline;
    setActiveFormatting((prev) => ({ ...prev, underline: newUnderlineState }));

    if (newUnderlineState) {
      editor.chain().focus().toggleUnderline().run();
    } else {
      editor.chain().focus().unsetUnderline().run();
    }
  };

  const handleStrikeToggle = () => {
    const newStrikeState = !activeFormatting.strike;
    setActiveFormatting((prev) => ({ ...prev, strike: newStrikeState }));

    if (newStrikeState) {
      editor.chain().focus().toggleStrike().run();
    } else {
      editor.chain().focus().unsetStrike().run();
    }
  };

  const extractTaggedUsers = useCallback((editor: Editor): string[] => {
    const { doc } = editor.state;
    const taggedUserIds: string[] = [];

    doc.descendants((node) => {
      if (node.type.name === "mention") {
        const userId = node.attrs.id;
        if (userId && !taggedUserIds.includes(userId)) {
          taggedUserIds.push(userId);
        }
      }
    });

    return taggedUserIds;
  }, []);

  const updateTaggedUsers = useCallback(
    (editor: Editor) => {
      if (setTaggedUsers) {
        const taggedUserIds = extractTaggedUsers(editor);
        setTaggedUsers(taggedUserIds);
      }
    },
    [setTaggedUsers, extractTaggedUsers]
  );

  const mentionsControl = mentionsSchema.mentions ? (
    <div ref={mentionsRef} className="relative">
      <div className="flex items-center">
        <Tooltip content="Mention" side="bottom">
          <button
            type="button"
            className="p-1 rounded hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
            onClick={() => setIsMentionsOpen(!isMentionsOpen)}
          >
            <MentionsIcon className="w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk" />
          </button>
        </Tooltip>
      </div>

      {mentionsPortal.renderPortal(
        <div
          data-mentions-dropdown
          className={`flex flex-col w-[200px] max-h-[190px] overflow-y-auto bg-popper-bg dark:bg-popper-bg-dk border border-border-gray dark:border-border-gray-dk rounded-[8px] shadow-popper-blur-effect backdrop-blur-[10px] ${visibleScrollbar(
            "[&::-webkit-scrollbar]:h-[8px] [&::-webkit-scrollbar]:w-[8px]"
          )}`}
        >
          {mentionActiveUsersList?.length ? (
            mentionActiveUsersList.map(
              (
                user: {
                  _id: string;
                  name: string;
                  email: string;
                  profile_picture: string | null;
                },
                index: number
              ) => (
                <button
                  type="button"
                  key={index}
                  onClick={() => {
                    editor
                      .chain()
                      .focus()
                      .insertContent({
                        type: "mention",
                        attrs: { id: user._id, label: user.name },
                      })
                      .run();
                    setIsMentionsOpen(false);
                    updateTaggedUsers(editor);
                  }}
                  className="px-[12px] py-[10px] hover:bg-hover-effect dark:hover:bg-hover-effect-dk cursor-pointer"
                >
                  <div className="flex items-center gap-[5px]">
                    {user.profile_picture ? (
                      <img
                        src={user.profile_picture}
                        alt={user.name}
                        className="w-6 h-6 rounded-full"
                      />
                    ) : (
                      <Avatar text={user.name} size={24} />
                    )}

                    <p className="font-medium text-md text-heading-text dark:text-heading-text-dk">
                      {user.name}
                    </p>
                  </div>
                </button>
              )
            )
          ) : (
            <p className="px-[12px] py-[10px] text-center font-medium text-md text-heading-text dark:text-heading-text-dk">
              No users found
            </p>
          )}
        </div>
      )}
    </div>
  ) : null;

  const linkControl = mediaSchema.links ? (
    <div className="relative" ref={linkPopoverRef}>
      <Tooltip content="Link" side="bottom">
        <button
          type="button"
          onClick={handleLinkButtonClick}
          className={`p-1 rounded ${
            isLinkPopoverOpen
              ? "bg-gray-50 dark:bg-gray-50-dk"
              : "hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
          }`}
        >
          <LinkIcon className="w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk" />
        </button>
      </Tooltip>

      {linkPortal.renderPortal(
        <div className="w-80 bg-pure-white dark:bg-verba-indigo border border-border-gray dark:border-gray-375 rounded-[4px] shadow-lg p-3 space-y-3">
          <div className="space-y-1">
            <input
              id="link-text"
              type="text"
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
              className="w-full px-2 py-1 border border-border-gray dark:border-gray-375 rounded focus:outline-none text-sm font-normal text-verba-indigo dark:text-verba-indigo-dk bg-pure-white dark:bg-verba-indigo"
              placeholder="Text"
            />
          </div>
          <div className="space-y-1">
            <input
              id="link-url"
              type="text"
              value={linkUrl}
              onChange={handleLinkUrlChange}
              className={`w-full px-2 py-1 border rounded focus:outline-none text-sm font-normal text-verba-indigo dark:text-verba-indigo-dk bg-pure-white dark:bg-verba-indigo ${
                linkUrlError
                  ? "border-verba-red-alert dark:border-verba-red-alert-dk"
                  : "border-border-gray dark:border-gray-375"
              }`}
              placeholder="Type or paste a link"
              autoFocus
            />
            {linkUrlError && (
              <div className="text-xs text-verba-red-alert dark:text-verba-red-alert-dk font-medium">
                {linkUrlError}
              </div>
            )}
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleApplyLink}
              disabled={!linkUrl || !linkText || !!linkUrlError}
              className="disabled:opacity-50 px-4 py-1 text-sm font-medium text-fixed-white dark:text-fixed-white-dk bg-verba-blue dark:bg-verba-blue-dk rounded"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  ) : null;

  return (
    <div
      className={`flex items-center gap-[4px] shadow-custom-lg rounded-[2px] max-w-fit ${toolbarExtraClass}`}
    >
      {/* History Controls - Undo and Redo Buttons */}
      {mergedSchema.history && (
        <>
          <Tooltip content="Undo" side="top">
            <button
              type="button"
              onClick={() => editor.chain().focus().undo().run()}
              disabled={!editor.can().undo()}
            >
              <UndoIcon className="w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk" />
            </button>
          </Tooltip>
          <Tooltip content="Redo" side="top">
            <button
              type="button"
              onClick={() => editor.chain().focus().redo().run()}
              disabled={!editor.can().redo()}
            >
              <RedoIcon className="w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk" />
            </button>
          </Tooltip>
        </>
      )}

      {/* Typography Controls */}
      {typographySchema.fontFamily && (
        <div className="relative" ref={fontDropdownRef}>
          <Tooltip content="Font Family" side="top">
            <button
              type="button"
              onClick={() => setIsFontOpen(!isFontOpen)}
              className="flex items-center justify-between space-x-2 bg-gray-75 dark:bg-gray-375 rounded-[4px] py-1 px-2 text-sm font-medium text-verba-indigo dark:text-verba-indigo-dk"
            >
              <span className="truncate">
                {
                  fontOptions.find((option) => option.value === selectedFont)
                    ?.label
                }
              </span>
              <ChevronDownSolidIcon
                className={`w-[10px] h-[5px] text-verba-indigo dark:text-verba-indigo-dk transition-transform duration-200 ${
                  isFontOpen ? "rotate-180" : ""
                }`}
              />
            </button>
          </Tooltip>
          {fontPortal.renderPortal(
            <div className="bg-popper-bg dark:bg-popper-bg-dk border border-border-gray dark:border-border-gray-dk rounded-[8px] shadow-popper-blur-effect backdrop-blur-[10px] min-w-[150px] max-h-44 overflow-y-auto no-scrollbar">
              <ul className="py-1">
                {fontOptions.map((option) => (
                  <li key={option.value}>
                    <button
                      type="button"
                      onClick={() => handleFontChange(option.value)}
                      className={`w-full px-2 py-2 text-left text-sm hover:bg-hover-effect dark:hover:bg-hover-effect-dk focus:bg-card-highlight dark:focus:bg-card-highlight-dk focus:outline-none flex items-center ${
                        selectedFont === option.value
                          ? "bg-card-highlight dark:bg-card-highlight-dk text-verba-blue dark:text-verba-blue-dk font-medium"
                          : "text-verba-indigo dark:text-verba-indigo-dk"
                      }`}
                    >
                      <span className="w-5 mr-1">
                        {selectedFont === option.value && <CheckIcon />}
                      </span>
                      <span
                        style={{ fontFamily: option.value }}
                        className="text-verba-indigo dark:text-verba-indigo-dk"
                      >
                        {option.label}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {typographySchema.fontSize && (
        <div className="relative" ref={sizeDropdownRef}>
          <Tooltip content="Font Size" side="top">
            <button
              type="button"
              onClick={() => setIsSizeOpen(!isSizeOpen)}
              className="flex items-center justify-between space-x-1 bg-gray-75 dark:bg-gray-375 rounded-[4px] py-1 px-2 text-sm font-medium text-verba-indigo dark:text-verba-indigo-dk"
            >
              <FontSizeIcon className="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk" />
              <ChevronDownSolidIcon
                className={`w-[10px] h-[5px] text-verba-indigo dark:text-verba-indigo-dk transition-transform duration-200 ${
                  isSizeOpen ? "rotate-180" : ""
                }`}
              />
            </button>
          </Tooltip>

          {sizePortal.renderPortal(
            <div className="min-w-[120px] bg-pure-white dark:bg-popper-bg-dk border border-border-gray dark:border-border-gray-dk rounded-[8px] shadow-popper-blur-effect backdrop-blur-[10px]">
              <ul className="py-1">
                {sizeOptions.map((option) => (
                  <li key={option.value}>
                    <button
                      type="button"
                      onClick={() => handleSizeChange(option.value)}
                      className={`w-full px-2 py-2 text-left text-sm hover:bg-hover-effect dark:hover:bg-hover-effect-dk focus:bg-card-highlight dark:focus:bg-card-highlight-dk focus:outline-none flex items-center ${
                        selectedSize === option.value
                          ? "bg-card-highlight dark:bg-card-highlight-dk text-verba-blue dark:text-verba-blue-dk font-medium"
                          : "text-verba-indigo dark:text-verba-indigo-dk"
                      }`}
                    >
                      <span className="w-5 mr-1">
                        {selectedSize === option.value && <CheckIcon />}
                      </span>
                      <span
                        style={{ fontSize: option.fontSize }}
                        className="text-verba-indigo dark:text-verba-indigo-dk"
                      >
                        {option.label}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Text Formatting Controls */}
      {(textFormattingSchema.bold ||
        textFormattingSchema.italic ||
        textFormattingSchema.underline ||
        (groupedToolbarOrder && textFormattingSchema.strikethrough)) && (
        <div className="flex items-center">
          {textFormattingSchema.bold && (
            <Tooltip content="Bold" side="top">
              <button
                type="button"
                onClick={handleBoldToggle}
                className={`p-1 rounded ${
                  activeFormatting.bold
                    ? "bg-gray-50 dark:bg-gray-50-dk"
                    : "hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                }`}
              >
                <BoldIcon className="w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk" />
              </button>
            </Tooltip>
          )}
          {textFormattingSchema.italic && (
            <Tooltip content="Italics" side="top">
              <button
                type="button"
                onClick={handleItalicToggle}
                className={`p-1 rounded ${
                  activeFormatting.italic
                    ? "bg-gray-50 dark:bg-gray-50-dk"
                    : "hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                }`}
              >
                <ItalicsIcon className="w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk" />
              </button>
            </Tooltip>
          )}
          {textFormattingSchema.underline && (
            <Tooltip content="Underline" side="top">
              <button
                type="button"
                onClick={handleUnderlineToggle}
                className={`p-1 rounded ${
                  activeFormatting.underline
                    ? "bg-gray-50 dark:bg-gray-50-dk"
                    : "hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                }`}
              >
                <UnderlineIcon className="w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk" />
              </button>
            </Tooltip>
          )}
          {groupedToolbarOrder && textFormattingSchema.strikethrough && (
            <Tooltip content="Strikethrough" side="top">
              <button
                type="button"
                onClick={handleStrikeToggle}
                className={`p-1 rounded ${
                  activeFormatting.strike
                    ? "bg-gray-50 dark:bg-gray-50-dk"
                    : "hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                }`}
              >
                <StrikeThroughIcon className="w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk" />
              </button>
            </Tooltip>
          )}
        </div>
      )}

      {/* Text Styling Controls */}
      {textStylingSchema.textColor && (
        <div className="relative" ref={colorDropdownRef}>
          <Tooltip content="Text Color" side="top">
            <button
              type="button"
              onClick={() => setIsColorOpen(!isColorOpen)}
              className="p-1 rounded flex items-center justify-between space-x-1 hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
            >
              <TextColorPickerIcon className="w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk" />
              <ChevronDownSolidIcon
                className={`w-[10px] h-[5px] text-gray-placeholder dark:text-gray-placeholder-dk transition-transform duration-200 ${
                  isColorOpen ? "rotate-180" : ""
                }`}
              />
            </button>
          </Tooltip>

          {colorPortal.renderPortal(
            <div className="bg-popper-bg dark:bg-popper-bg-dk border border-border-gray dark:border-border-gray-dk rounded-[8px] shadow-popper-blur-effect backdrop-blur-[10px]">
              <ColorPicker
                editor={editor}
                onSetColor={handleSetColor}
                onUnsetColor={handleUnsetColor}
                onSetHighlight={handleSetHighlight}
                onUnsetHighlight={handleUnsetHighlight}
              />
            </div>
          )}
        </div>
      )}

      {/* Content Structure Controls */}
      {contentStructureSchema.lists && (
        <div className="relative" ref={listDropdownRef}>
          <Tooltip content="List" side="top">
            <button
              type="button"
              onClick={() => setIsListOpen(!isListOpen)}
              className={`p-1 flex items-center justify-between space-x-1 rounded-[4px] text-sm font-medium ${
                editor.isActive("bulletList") || editor.isActive("orderedList")
                  ? "bg-gray-50 dark:bg-gray-50-dk"
                  : "hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
              }`}
            >
              {selectedList === "bullet" ? (
                <UnorderedListIcon className="w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk" />
              ) : (
                <NumberedListIcon className="w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk" />
              )}
              <ChevronDownSolidIcon
                className={`w-[10px] h-[5px] text-gray-placeholder dark:text-gray-placeholder-dk transition-transform duration-200 ${
                  isListOpen ? "rotate-180" : ""
                }`}
              />
            </button>
          </Tooltip>
          {listPortal.renderPortal(
            <div className="min-w-[45px] bg-popper-bg dark:bg-popper-bg-dk border border-border-gray dark:border-border-gray-dk rounded-[8px] shadow-popper-blur-effect backdrop-blur-[10px]">
              <ul className="py-1">
                {listOptions.map((option) => {
                  const IconComponent = option.icon;
                  const isActive =
                    (option.value === "bullet" &&
                      editor.isActive("bulletList")) ||
                    (option.value === "number" &&
                      editor.isActive("orderedList"));

                  return (
                    <li key={option.value}>
                      <button
                        type="button"
                        onClick={() => handleListChange(option.value)}
                        className={`w-full py-1 text-left text-sm hover:bg-gray-75 dark:hover:bg-gray-375 focus:bg-gray-75 dark:focus:bg-gray-375 focus:outline-none flex items-center justify-center ${
                          isActive
                            ? "bg-gray-50 dark:bg-gray-50-dk"
                            : "hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                        }`}
                      >
                        <IconComponent className="w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}

      {contentStructureSchema.alignment && (
        <div className="relative" ref={alignDropdownRef}>
          <Tooltip content="Align" side="top">
            <button
              type="button"
              onClick={() => setIsAlignOpen(!isAlignOpen)}
              className="p-1 flex items-center justify-between space-x-1 hover:bg-hover-effect dark:hover:bg-hover-effect-dk rounded-[4px] text-sm font-medium text-verba-indigo dark:text-verba-indigo-dk"
            >
              {selectedAlign === "left" ? (
                <AlignLeftIcon className="w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk" />
              ) : selectedAlign === "center" ? (
                <AlignCenterIcon className="w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk" />
              ) : (
                <AlignRightIcon className="w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk" />
              )}
              <ChevronDownSolidIcon
                className={`w-[10px] h-[5px] text-gray-placeholder dark:text-gray-placeholder-dk transition-transform duration-200 ${
                  isAlignOpen ? "rotate-180" : ""
                }`}
              />
            </button>
          </Tooltip>

          {alignPortal.renderPortal(
            <div className="min-w-[45px] bg-popper-bg dark:bg-popper-bg-dk border border-border-gray dark:border-border-gray-dk rounded-[8px] shadow-popper-blur-effect backdrop-blur-[10px]">
              <ul className="py-1">
                {alignOptions.map((option) => {
                  const IconComponent = option.icon;
                  const isActive = selectedAlign === option.value;
                  return (
                    <li key={option.value}>
                      <button
                        type="button"
                        onClick={() => handleAlignChange(option.value)}
                        className={`w-full py-1 text-left text-sm hover:bg-hover-effect dark:hover:bg-hover-effect-dk focus:outline-none flex items-center justify-center text-verba-indigo dark:text-verba-indigo-dk ${
                          isActive
                            ? "bg-gray-50 dark:bg-gray-50-dk"
                            : "hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                        }`}
                      >
                        <IconComponent className="w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}

      {contentStructureSchema.indentation && (
        <div className="flex items-center space-x-1">
          <Tooltip content="Text indent less" side="top">
            <button
              type="button"
              onClick={handleOutdent}
              className="p-1 rounded hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
            >
              <IndentLeftIcon className="w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk" />
            </button>
          </Tooltip>
          <Tooltip content="Text indent more" side="top">
            <button
              type="button"
              onClick={handleIndent}
              className="p-1 rounded hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
            >
              <IndentRightIcon className="w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk" />
            </button>
          </Tooltip>
        </div>
      )}

      {/* Media Controls (default position, non-grouped) */}
      {!groupedToolbarOrder && mediaSchema.links && (
        <div className="flex items-center">{linkControl}</div>
      )}

      {mediaSchema.images && (
        <div className="flex items-center">
          <Tooltip content="Image" side="bottom">
            <button
              type="button"
              onClick={handleImageUpload}
              className="p-1 rounded hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
            >
              <ImageIcon className="w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk" />
            </button>
          </Tooltip>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageFileSelect}
            className="hidden"
          />
        </div>
      )}

      {mediaSchema.attachments && (
        <div className="flex items-center">
          <Tooltip
            content={isProcessingAttachments ? "Processing..." : "Attach Files"}
            side="bottom"
          >
            <button
              type="button"
              onClick={handleAttachmentClick}
              disabled={isProcessingAttachments}
              className={`p-1 rounded transition-colors ${
                isProcessingAttachments
                  ? "bg-gray-50 dark:bg-gray-50-dk"
                  : "hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
              }`}
            >
              <AttachmentIcon
                className={`w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk ${
                  isProcessingAttachments ? "animate-pulse" : ""
                }`}
              />
            </button>
          </Tooltip>
          <input
            ref={attachmentInputRef}
            type="file"
            multiple
            onChange={handleAttachmentFileSelect}
            className="hidden"
          />
        </div>
      )}

      {/* Interactive Controls */}
      {interactiveSchema.emojis && (
        <div className="relative" ref={emojiDropdownRef}>
          <Tooltip content="Emoji" side="bottom">
            <button
              type="button"
              onClick={handleEmojiToggle}
              className={`p-1 rounded ${
                isEmojiOpen
                  ? "bg-gray-50 dark:bg-gray-50-dk"
                  : "hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
              }`}
            >
              <EmojiIcon className="w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk" />
            </button>
          </Tooltip>

          {emojiPortal.renderPortal(
            <EmojiPicker
              editor={editor}
              onClose={() => setIsEmojiOpen(false)}
            />
          )}
        </div>
      )}

      {/* Utility Controls */}
      {(utilitiesSchema.clearFormatting ||
        (textFormattingSchema.strikethrough && !groupedToolbarOrder) ||
        contentStructureSchema.blockquotes) && (
        <div className="flex items-center space-x-1">
          {formattingOptions.map((option) => {
            const shouldShow =
              (option.value === "remove" && utilitiesSchema.clearFormatting) ||
              (option.value === "strike" &&
                textFormattingSchema.strikethrough &&
                !groupedToolbarOrder) ||
              (option.value === "quote" && contentStructureSchema.blockquotes);

            if (!shouldShow) return null;

            const IconComponent = option.icon;
            const isActive =
              option.value === "strike"
                ? activeFormatting.strike
                : option.value === "remove"
                  ? false
                  : option.value === "quote"
                    ? editor.isActive("blockquote")
                    : false;

            const tooltipLabel =
              option.value === "strike"
                ? "Strikethrough"
                : option.value === "remove"
                  ? "Clear Formatting"
                  : option.value === "quote"
                    ? "Blockquote"
                    : "";

            return (
              <Tooltip content={tooltipLabel} side="top" key={option.value}>
                <button
                  type="button"
                  onClick={
                    option.value === "strike"
                      ? handleStrikeToggle
                      : option.value === "remove"
                        ? () =>
                            editor
                              .chain()
                              .focus()
                              .clearNodes()
                              .unsetAllMarks()
                              .run()
                        : option.value === "quote"
                          ? () =>
                              editor.chain().focus().toggleBlockquote().run()
                          : undefined
                  }
                  className={`p-1 rounded ${
                    isActive
                      ? "bg-gray-50 dark:bg-gray-50-dk"
                      : "hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                  }`}
                >
                  <IconComponent
                    className={
                      option.iconClassName ||
                      "w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk"
                    }
                  />
                </button>
              </Tooltip>
            );
          })}
        </div>
      )}

      {/* Mentions Controls (default position, non-grouped) */}
      {!groupedToolbarOrder && mentionsControl}

      {/* Clubbed Mention + Link together at the end (grouped layout) */}
      {groupedToolbarOrder && (mentionsControl || linkControl) && (
        <>
          <div className="mx-[6px] h-[19px] w-px bg-border-gray dark:bg-border-gray-dk shrink-0" />
          <div className="flex items-center gap-[4px]">
            {mentionsControl}
            {linkControl}
          </div>
        </>
      )}
    </div>
  );
};

export default Toolbar;
